/**
 * Mobile navigation: fullscreen panel with GSAP open/close + mega-menu accordions.
 */
import gsap from "gsap";

const MQ = "(max-width: 1024px)";
const EASE_OUT = "power3.out";
const EASE_IN = "power2.in";

function isMobileNav() {
	return window.matchMedia(MQ).matches;
}

function lockScroll(lock) {
	document.documentElement.classList.toggle("mobile-nav-is-open", lock);
	document.body.classList.toggle("mobile-nav-is-open", lock);
}

export function initMobileNav() {
	const toggle = document.querySelector(".header__nav-toggle");
	const nav = document.querySelector("#site-navigation");
	const navList = nav?.querySelector(".header__nav");

	if (!toggle || !nav || !navList) {
		return;
	}

	const topLevelItems = navList.querySelectorAll(":scope > li");
	const navContact = nav.querySelector(".header__nav-contact");
	const megaParents = navList.querySelectorAll(
		":scope > li.nav-services, :scope > li.nav-industries"
	);

	function getAnimTargets() {
		const items = Array.from(topLevelItems);
		return navContact ? [...items, navContact] : items;
	}

	let isOpen = false;
	let openTl = null;
	let closeTl = null;
	let lastFocus = null;

	function setToggleState(open) {
		toggle.setAttribute("aria-expanded", open ? "true" : "false");
		toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
		toggle.classList.toggle("is-open", open);
		nav.classList.toggle("is-open", open);
		lockScroll(open);
	}

	function killTimelines() {
		if (openTl) {
			openTl.kill();
			openTl = null;
		}
		if (closeTl) {
			closeTl.kill();
			closeTl = null;
		}
	}

	function closeAllMegaPanels() {
		megaParents.forEach((li) => {
			li.classList.remove("is-mobile-expanded");
			const trigger = li.querySelector(":scope > a");
			const dropdown = li.querySelector(".nav-services-dropdown");
			if (trigger) {
				trigger.setAttribute("aria-expanded", "false");
			}
			if (dropdown) {
				gsap.killTweensOf(dropdown);
				gsap.set(dropdown, { clearProps: "height,opacity,overflow" });
			}
		});
	}

	function openNav() {
		if (!isMobileNav() || isOpen) {
			return;
		}

		killTimelines();
		closeAllMegaPanels();

		lastFocus = document.activeElement;
		isOpen = true;
		setToggleState(true);

		nav.removeAttribute("aria-hidden");

		const targets = getAnimTargets();

		gsap.set(nav, { visibility: "visible", pointerEvents: "auto", opacity: 0 });
		gsap.set(targets, { opacity: 0, y: 24 });

		openTl = gsap.timeline({
			onComplete: () => {
				openTl = null;
			},
		});

		openTl.to(nav, { opacity: 1, duration: 0.32, ease: EASE_OUT }, 0);

		openTl.to(
			targets,
			{
				opacity: 1,
				y: 0,
				duration: 0.42,
				stagger: 0.07,
				ease: EASE_OUT,
			},
			0.1
		);
	}

	function closeNav() {
		if (!isOpen) {
			return;
		}

		killTimelines();
		closeAllMegaPanels();

		const targets = getAnimTargets();

		closeTl = gsap.timeline({
			onComplete: () => {
				isOpen = false;
				setToggleState(false);
				nav.setAttribute("aria-hidden", "true");
				gsap.set(nav, {
					visibility: "hidden",
					pointerEvents: "none",
					clearProps: "opacity",
				});
				gsap.set(targets, { clearProps: "opacity,y" });
				if (lastFocus && typeof lastFocus.focus === "function") {
					lastFocus.focus();
				}
				closeTl = null;
			},
		});

		closeTl.to(
			targets,
			{
				opacity: 0,
				y: 14,
				duration: 0.2,
				stagger: 0.035,
				ease: EASE_IN,
			},
			0
		);

		closeTl.to(nav, { opacity: 0, duration: 0.22, ease: EASE_IN }, 0.05);
	}

	function toggleNav() {
		if (!isMobileNav()) {
			return;
		}
		if (isOpen) {
			closeNav();
		} else {
			openNav();
		}
	}

	function toggleMegaPanel(li) {
		const trigger = li.querySelector(":scope > a");
		const dropdown = li.querySelector(".nav-services-dropdown");
		if (!trigger || !dropdown) {
			return;
		}

		const willExpand = !li.classList.contains("is-mobile-expanded");

		if (willExpand) {
			li.classList.add("is-mobile-expanded");
			trigger.setAttribute("aria-expanded", "true");

			gsap.killTweensOf(dropdown);
			gsap.set(dropdown, { height: "auto", overflow: "hidden", opacity: 0 });
			const targetHeight = dropdown.offsetHeight;
			gsap.fromTo(
				dropdown,
				{ height: 0, opacity: 0 },
				{
					height: targetHeight,
					opacity: 1,
					duration: 0.38,
					ease: EASE_OUT,
					onComplete: () => {
						gsap.set(dropdown, { clearProps: "height,overflow,opacity" });
					},
				}
			);
		} else {
			collapseMegaPanel(li);
		}
	}

	function collapseMegaPanel(li) {
		const trigger = li.querySelector(":scope > a");
		const dropdown = li.querySelector(".nav-services-dropdown");
		if (!trigger || !dropdown || !li.classList.contains("is-mobile-expanded")) {
			return;
		}

		gsap.killTweensOf(dropdown);
		gsap.set(dropdown, { height: dropdown.offsetHeight, overflow: "hidden" });

		gsap.to(dropdown, {
			height: 0,
			opacity: 0,
			duration: 0.28,
			ease: EASE_IN,
			onComplete: () => {
				li.classList.remove("is-mobile-expanded");
				trigger.setAttribute("aria-expanded", "false");
				gsap.set(dropdown, { clearProps: "height,opacity,overflow" });
			},
		});
	}

	function setClosedState() {
		if (!isMobileNav()) {
			nav.removeAttribute("aria-hidden");
			gsap.set(nav, { clearProps: "visibility,opacity,pointerEvents" });
			return;
		}

		if (!isOpen) {
			nav.setAttribute("aria-hidden", "true");
			gsap.set(nav, { visibility: "hidden", opacity: 0, pointerEvents: "none" });
		}
	}

	setClosedState();

	toggle.addEventListener("click", toggleNav);

	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && isOpen) {
			closeNav();
		}
	});

	megaParents.forEach((li) => {
		const trigger = li.querySelector(":scope > a");
		if (!trigger) {
			return;
		}

		trigger.setAttribute("aria-haspopup", "true");
		trigger.setAttribute("aria-expanded", "false");

		trigger.addEventListener("click", (e) => {
			if (!isMobileNav()) {
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			toggleMegaPanel(li);
		});
	});

	navList.querySelectorAll("a").forEach((link) => {
		link.addEventListener("click", (e) => {
			if (!isMobileNav() || !isOpen) {
				return;
			}

			const megaLi = link.closest("li.nav-services, li.nav-industries");
			if (megaLi && link === megaLi.querySelector(":scope > a")) {
				return;
			}

			e.stopPropagation();
			closeNav();
		});
	});

	if (navContact) {
		navContact.querySelectorAll("a").forEach((link) => {
			link.addEventListener("click", () => {
				if (isMobileNav() && isOpen) {
					closeNav();
				}
			});
		});
	}

	window.matchMedia(MQ).addEventListener("change", (e) => {
		if (!e.matches && isOpen) {
			closeNav();
		}
		setClosedState();
	});
}
